// Single source of copy for account-standing-aware presentation, keyed off
// the same {status, warningState} shape getAccountStanding() (backend
// enforcementService.js) returns. errors/errorMessages.js still owns
// errorCode -> base moderation-block copy (Part A) — this config adds the
// standing-aware layer that AccountStandingSection.js renders on /account.
//
// The moderation-block error-banner addendum itself no longer lives here —
// it's sourced directly from the block response's own
// `enforcementPresentation` field (see config/enforcementPresentation.js),
// not recomputed from a separately-fetched `standing` value, since a block
// can escalate the account as a side effect of the same request and a
// second fetch could lose that race. This file's remaining exports are
// still standing-driven and correct for that: STATUS_INFO/
// APPROACHING_RESTRICTION_INFO describe the account's *current* resting
// state on /account, not "what just happened" in a specific request.

// Text pipeline's two moderation-block codes (battleResult.js) — mirrored in
// useBattleRequest.js/App.js. The image pipeline has its own local
// MODERATION_ERROR_CODES in BattleImage.js (3 codes, includes the provider-
// refusal "moderation") — not merged with this one, since the two pipelines
// genuinely have different code sets.
export const TEXT_MODERATION_BLOCK_CODES = ["moderation_blocked", "moderation_output_blocked"];

// Ambient standing nudge for the image pipeline (BattleImage.js): unlike
// the text pipeline's battle_input block, an image-moderation block never
// itself changes Account Standing (the entire image pipeline is
// non-scoring — see errors/moderation.js's logImageSafetyEvent), so there's
// no per-request transition to report via enforcementPresentation. This is
// purely an FYI based on the account's *existing* standing, shown alongside
// an otherwise-unrelated image error — a different, lower-stakes purpose
// than config/enforcementPresentation.js's transition-specific copy.
export function getStandingNudge(standing) {
  if (!standing) return null;
  if (standing.warningState === "approaching_restriction") {
    return { text: APPROACHING_RESTRICTION_INFO.message, actionLabel: "View Account Standing", actionPath: "/account" };
  }
  if (standing.status === "limited") {
    return { text: STATUS_INFO.limited.message, actionLabel: "View Account Standing", actionPath: "/account" };
  }
  return null;
}

// Tier 2, rendered proactively on /account — good_standing status but
// LEVEL_1_WATCH underneath, so there's nothing "wrong" yet, just a heads-up.
export const APPROACHING_RESTRICTION_INFO = {
  label: "Approaching Restriction",
  message: "Recent activity is close to triggering a temporary restriction. Review our Rules & Code of Conduct to avoid further action.",
};

// Tier 3/4: per-status label/message for the already-working Limited/
// Suspended/Under Review/Banned rendering — moved here, unchanged, so
// there's one place defining all 4 tiers' copy instead of two.
export const STATUS_INFO = {
  limited: { label: "Limited", message: "Some account features are temporarily limited." },
  suspended: { label: "Temporarily Suspended", message: "Battle generation is temporarily suspended for this account." },
  under_review: { label: "Under Review", message: "This account is currently suspended and has been flagged for review." },
  banned: { label: "Banned", message: "Account generation access has been banned." },
};
